import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  QueryList,
  Self,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { DataSourceParams, PageableDataSource, SelectOption, TemplateDirective } from '@fleet-beat/core';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BehaviorSubject, Observable, delay, filter } from 'rxjs';
import { SimpleField } from '../../abstracts';

@Component({
  selector: 'fb-data-select',
  templateUrl: './data-select.component.html',
  styleUrls: ['./data-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataSelectComponent extends SimpleField<boolean | string | number | object> implements OnInit, OnDestroy {
  @Input()
  set dataStorage(storage: PageableDataSource<Observable<SelectOption[]>, DataSourceParams<object, object>>) {
    this.localDataStorage = storage;
    this.reloadWithDelay.next();
  }
  get dataStorage(): PageableDataSource<Observable<SelectOption[]>, DataSourceParams<object, object>> {
    return this.localDataStorage;
  }
  get selectAllButtonText(): string {
    return this.selectAllSelected ? 'Deselect All' : 'Select All';
  }

  @Input() options: SelectOption[];
  @Input() multiple: boolean = false;
  @Input() selectAll: boolean = false;
  @Input() displayLabel: boolean;
  @Input() templates: QueryList<TemplateDirective<object>>;

  selectedCounter: number;
  selectedOptions: SelectOption[] = [];
  selectAllSelected: boolean = false;
  private localDataStorage: PageableDataSource<Observable<SelectOption[]>, DataSourceParams<object, object>>;
  private reloadWithDelay: BehaviorSubject<void> = new BehaviorSubject(undefined);

  constructor(
    protected override changeDetectorRef: ChangeDetectorRef,
    @Optional() @Self() public override ngControl: NgControl,
  ) {
    super(changeDetectorRef, ngControl);
  }

  ngOnInit(): void {
    this.setListeners();
  }

  override ngOnDestroy(): void {
    if (this.multiple) {
      this.onClear();
    }
    super.ngOnDestroy();
  }
  toggleSelectAll(): void {
    if (this.selectAllSelected) {
      this.ngControl.control.setValue([]);
      this.selectedOptions = [];
    } else {
      const allValues = this.options.map((option: SelectOption) => option.value);
      this.ngControl.control.setValue(allValues);
      this.selectedOptions = [...this.options];
    }
    this.selectAllSelected = !this.selectAllSelected;
    this.selectedCounter = this.selectedOptions.length;
    this.changeDetectorRef.markForCheck();
  }
  onClear(): void {
    this.selectedCounter = null;
    this.ngControl.control.reset();
    this.selectedOptions = [];
  }
  isLast(option: SelectOption): boolean {
    return this.selectedOptions.indexOf(option) === this.selectedOptions.length - 1;
  }
  private setListeners(): void {
    this.ngControl?.control.valueChanges
      .pipe(untilComponentDestroyed(this), filter(Boolean))
      .subscribe((value: SelectOption['value'][] | string | number | boolean | object) => {
        if (Array.isArray(value)) {
          this.selectedCounter = value?.length;
          this.selectedOptions = this.options.filter((option: SelectOption) => value?.includes(option.value));
        }
        this.changeDetectorRef.markForCheck();
      });
    this.reloadWithDelay
      .pipe(
        delay(300),
        filter(() => Boolean(this.localDataStorage && this.localDataStorage.reloadSubject)),
        untilComponentDestroyed(this),
      )
      .subscribe(() => this.localDataStorage.reloadSubject.next({}));
  }
}
